import React from 'react';
import { Shield, Lock, Eye, Edit, Trash2, Download, XCircle, CheckCircle, Clock, Database } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const GDPRPage = () => {
  const dataTypes = [
    'Identification and contact details (e.g. name, email address, phone number, company name, country of residence)',
    'Information you voluntarily provide in forms, emails, or during communication with us',
    'Technical data related to your visit to our website (e.g. IP address, browser type, pages visited), often collected via cookies or similar technologies'
  ];

  const gdprRights = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Access',
      description: 'Access your personal data and receive a copy'
    },
    {
      icon: <Edit className="w-6 h-6" />,
      title: 'Rectification',
      description: 'Rectify inaccurate or incomplete data'
    },
    {
      icon: <Trash2 className="w-6 h-6" />,
      title: 'Erasure',
      description: 'Erase your data ("right to be forgotten") in certain circumstances'
    },
    {
      icon: <XCircle className="w-6 h-6" />,
      title: 'Restriction/Objection',
      description: 'Restrict or object to certain types of processing'
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: 'Data Portability',
      description: 'Data portability, where applicable'
    },
    {
      icon: <CheckCircle className="w-6 h-6" />,
      title: 'Withdraw Consent',
      description: 'Withdraw consent at any time, where processing is based on consent'
    }
  ];

  const retentionReasons = [
    'Provide our services',
    'Comply with legal, regulatory, and AML/KYC requirements',
    'Resolve disputes and enforce our agreements'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0d6571] to-[#0d6571]/80 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-6">
            <Shield className="w-12 h-12 text-white" />
            <h1 className="text-5xl font-bold text-white">
              GDPR & Data Protection
            </h1>
          </div>
          <p className="text-xl text-white/90 max-w-4xl">
            Your privacy matters. Learn how we protect and process your personal data.
          </p>
          <p className="text-sm text-white/80 mt-4">
            Last Updated: November 2024
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          
          {/* Introduction */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4">
              <Shield className="w-8 h-8 text-[#0d6571] flex-shrink-0 mt-1" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Commitment to Data Protection</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>FXC Management Services B.V.</strong> (FXC Management Services), located in the Netherlands, 
                  processes personal data in accordance with the <strong>General Data Protection Regulation (GDPR)</strong> and 
                  applicable Dutch data protection laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We are committed to protecting your privacy and ensuring that your personal data is handled 
                  responsibly, transparently, and securely.
                </p>
              </div>
            </div>
          </div>

          {/* Data Collection Purpose */}
          <div className="bg-gradient-to-br from-blue-50 to-white border-2 border-blue-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <Database className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Why We Collect Your Data</h2>
                <p className="text-sm text-gray-600">We only collect and process personal data that is necessary</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  Respond to Your Inquiries and Contact Requests
                </h3>
                <p className="text-gray-700 text-sm">
                  To communicate with you effectively and address your questions about our services.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  Provide Our Information and Consulting Services
                </h3>
                <p className="text-gray-700 text-sm">
                  To deliver the services you have requested and maintain our business relationship with you.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  Fulfil Legal and Regulatory Obligations
                </h3>
                <p className="text-gray-700 text-sm">
                  Including AML/KYC (Anti-Money Laundering and Know Your Customer) requirements where applicable.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 border-l-4 border-blue-500">
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                  Improve Our Website, Communications, and Service Quality
                </h3>
                <p className="text-gray-700 text-sm">
                  To enhance user experience and continuously improve our services based on your feedback and usage patterns.
                </p>
              </div>
            </div>
          </div>

          {/* Types of Data Collected */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
              <Database className="w-8 h-8 text-[#0d6571]" />
              Types of Data We May Collect
            </h2>
            
            <div className="space-y-4">
              {dataTypes.map((dataType, index) => (
                <div key={index} className="flex items-start gap-3 bg-gray-50 p-5 rounded-lg border border-gray-200">
                  <div className="w-2 h-2 rounded-full bg-[#0d6571] flex-shrink-0 mt-2"></div>
                  <p className="text-gray-700">{dataType}</p>
                </div>
              ))}
            </div>
          </div>

          {/* GDPR Rights */}
          <div className="bg-gradient-to-br from-green-50 to-white border-2 border-green-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <Shield className="w-10 h-10 text-green-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Your GDPR Rights</h2>
                <p className="text-sm text-gray-600">As a data subject under GDPR, you have the following rights</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {gdprRights.map((right, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-green-600">
                      {right.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{right.title}</h3>
                      <p className="text-gray-700 text-sm">{right.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 bg-green-100 border-l-4 border-green-600 p-6 rounded-r-lg">
              <p className="text-gray-800">
                <strong>To exercise any of these rights, or to ask questions about how we handle your data,</strong> 
                you can contact us via the details provided on this website.
              </p>
            </div>
          </div>

          {/* Data Retention & Security */}
          <div className="bg-gradient-to-br from-purple-50 to-white border-2 border-purple-200 rounded-xl shadow-lg p-8 mb-8">
            <div className="flex items-start gap-4 mb-6">
              <Lock className="w-10 h-10 text-purple-600 flex-shrink-0" />
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Data Retention & Security</h2>
                <p className="text-sm text-gray-600">How long we keep your data and how we protect it</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Data Retention */}
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-6 h-6 text-purple-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Data Retention</h3>
                </div>
                <p className="text-gray-700 mb-4">
                  We retain personal data <strong>only for as long as necessary</strong> to:
                </p>
                <div className="space-y-3">
                  {retentionReasons.map((reason, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg border border-purple-200">
                      <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{reason}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Security Measures */}
              <div className="bg-purple-100 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Lock className="w-6 h-6 text-purple-600" />
                  <h3 className="text-2xl font-bold text-gray-900">Security Measures</h3>
                </div>
                <p className="text-gray-800">
                  We take <strong>appropriate technical and organizational measures</strong> to protect your data 
                  against unauthorized access, loss, misuse, or disclosure. Our security practices are regularly 
                  reviewed and updated to ensure the highest level of protection.
                </p>
              </div>
            </div>
          </div>

          {/* Cookies Notice */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl shadow-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Database className="w-7 h-7 text-yellow-600" />
              Cookies and Tracking Technologies
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website uses cookies and similar tracking technologies to collect technical data about your visit. 
              This helps us improve our website functionality and user experience.
            </p>
            <p className="text-gray-700 leading-relaxed">
              For more detailed information about how we use cookies, please refer to our{' '}
              <a href="/cookie-policy" className="text-[#0d6571] font-semibold hover:text-[#d97841] underline">
                Cookie Policy
              </a>.
            </p>
          </div>

          {/* Contact Section */}
          <div className="bg-gradient-to-r from-[#0d6571] to-[#0d6571]/80 rounded-xl shadow-lg p-8 text-white text-center">
            <h2 className="text-2xl font-bold mb-4">Questions About Your Data?</h2>
            <p className="text-white/90 mb-6">
              If you have any questions about how we process your personal data, want to exercise your GDPR rights, 
              or have concerns about data protection, please don't hesitate to contact us.
            </p>
            <a
              href="/contact"
              className="inline-block px-8 py-3 bg-white text-[#0d6571] rounded-full font-bold hover:bg-gray-100 transition-colors"
            >
              Contact Us
            </a>
          </div>

          {/* Company Footer */}
          <div className="mt-8 text-center text-sm text-gray-600">
            <p className="font-semibold text-gray-900 mb-2">
              FXC Management Services B.V. (FXC Management Services)
            </p>
            <p>
              Registered Address: Industrieweg 9, 2254AE Voorschoten, The Netherlands
            </p>
            <p className="mt-2">
              Chamber of Commerce Registration: 78204992
            </p>
            <p className="mt-4 text-xs">
              Data Protection Officer: info@fxcvantage.com
            </p>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GDPRPage;
